<template>
    <div class="font-upload">
        <label v-if="showLabel" class="font-upload__label">
            <slot name="label" />
        </label>
        <div class="font-upload__upload-wrapper">
            <hox-loading-layer v-if="isUploading" :is-full-screen="false" size="small" />
            <Upload
                v-if="Object.keys(uploadConfig).length"
                :show-upload-list="false"
                :action="uploadConfig.action"
                :data="uploadConfig.data"
                :multiple="false"
                :before-upload="beforeUpload"
                :on-success="onSuccess"
                :on-error="onError"
                type="drag"
            >
                <div class="font-upload__uploader" data-testid="add-font-modal__upload-area">
                    <Icon size="38" type="ios-cloud-upload-outline" />
                    <p>Drag and drop (or click) to upload a font</p>
                </div>
            </Upload>
        </div>
        <p v-if="hasErrorUploading" class="font-upload__error-message">
            There was an error while uploading the font. Please try again in a few moments.
        </p>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
    props: {
        config: { type: Object }
    },

    emits: ["fontUploaded", "fontUploadFailed", "fontPreviewUpdate"],

    data() {
        return {
            hasErrorUploading: false,
            isUploading: false,
            uploadConfig: {},
            fileReader: new FileReader()
        };
    },
    computed: {
        showLabel() {
            return this.$slots.label !== undefined;
        }
    },
    watch: {
        config(c) {
            this.processConfig(c);
        }
    },
    mounted() {
        this.fileReader.onload = this.onFileReaderLoad;
        this.processConfig(this.config);
    },
    methods: {
        processConfig(config) {
            this.uploadConfig = { ...config };
            this.uploadConfig.data = JSON.parse(this.uploadConfig.postData);
            this.uploadConfig.data.key = this.uploadConfig.filePrefix;
        },
        beforeUpload(file) {
            this.uploadConfig.data.key += `/${file.name}`;

            const extension = file.name.split(".").pop();
            const isAcceptedFileType = ["otf", "ttf"].includes(extension);

            if (!isAcceptedFileType) {
                this.$snackbar.error("Font Error", "The font must be either OTF or TTF");
                return false;
            }

            const maxFileSizeInMb = 50;

            if (file.size / 1024 / 1024 > maxFileSizeInMb) {
                this.$snackbar.error("Font Error", `Font size can not exceed ${maxFileSizeInMb}MB`);
                return false;
            }

            this.fileReader.readAsDataURL(file);
            this.isUploading = true;

            return true;
        },
        onFileReaderLoad(e) {
            this.$emit("fontPreviewUpdate", e.target.result);
        },
        onSuccess() {
            this.isUploading = false;
            this.$emit("fontUploaded", this.uploadConfig.data.key);
        },
        onError() {
            this.isUploading = false;
            this.hasErrorUploading = true;
            this.$emit("fontUploadFailed");
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.font-upload {
    margin: 0 0 $spacing;

    .ivu-upload-drag {
        color: $link-color;
        transition: background, border-color, color;
        transition-timing-function: $transition-function-base;
        transition-duration: $transition-time-base;

        &:hover {
            color: $link-hover-color;
            border-color: $link-hover-color;
        }
    }

    .ivu-upload-dragOver {
        background: rgba($link-color, 0.15);
        border: 1px dashed $link-hover-color;
        color: $link-hover-color;
    }

    &__error-message {
        color: $error-color;
        font-size: $font-size-small;
        margin: 0 0 $spacing;
    }

    &__font {
        max-width: auto;
        max-height: 150px;
        display: block;
        border: 1px solid $cmp-light-border-color;
        padding: $spacing-smallest;
        margin: 0 0 $spacing-small;
    }

    &__label {
        color: $cmp-light-secondary-font-color;
        display: inline-block;
        font-size: $font-size-base;
        margin: 0 0 $spacing-small;
    }

    &__upload-wrapper {
        position: relative;
        margin: 0 0 $spacing-small;
    }

    &__uploader {
        font-size: $font-size-base;
        width: 100%;
        text-align: center;
        cursor: pointer;
        padding: $spacing-large $spacing;

        .ivu-icon {
            margin: 0 0 $spacing-small;
        }
    }
}
</style>
